/**
 * Mime bucketing and display names for `Media` rows.
 *
 * Two measured facts about the live table drive everything here (445,241
 * manuscript media rows, 2026-08-21):
 *
 * 1. **Mime spelling is fragmented.** RAR alone arrives as `application/vnd.rar`
 *    (63,829 rows), `application/x-rar-compressed` (628), `application/x-rar`
 *    (225) and `application/rar` (131); ZIP as `application/zip` (132,024) and
 *    `application/x-zip-compressed` (50). A bucket that matches only the
 *    canonical spelling silently mislabels tens of thousands of files, so every
 *    spelling is listed. Archives are 44.2% of the corpus and PDF 49.4% — this
 *    is not a long tail.
 * 2. **Metadata is missing in two disjoint ways.** 14,055 rows have NULL
 *    `file_size` (the photo insert path writes no size, name, width or height)
 *    and a *different* 8,331 rows have NULL `mime_type` (real documents,
 *    averaging 28 MB). Neither NULL is an error and neither implies the other,
 *    so nothing here treats a missing value as zero or as "unknown type".
 */

import { MediaFile } from '@/types/api';

export type MediaKind = 'image' | 'video' | 'audio' | 'pdf' | 'archive' | 'document' | 'file';

/**
 * Every archive spelling seen in the wild, canonical or not. `epub+zip` is
 * DELIBERATELY absent: it is a zip container, but it is a book to a reviewer
 * and gets the document label (and an `.epub` extension) instead.
 */
const ARCHIVE_MIMES = new Set([
  'application/zip',
  'application/x-zip-compressed',
  'application/vnd.rar',
  'application/x-rar-compressed',
  'application/x-rar',
  'application/rar',
  'application/x-7z-compressed',
  'application/x-tar',
  'application/gzip',
  'application/x-gzip',
]);

/** Only spellings we can name an extension for; anything else stays nameless. */
const MIME_EXTENSIONS: Record<string, string> = {
  'application/pdf': 'pdf',
  'application/zip': 'zip',
  'application/x-zip-compressed': 'zip',
  'application/epub+zip': 'epub',
  'application/vnd.rar': 'rar',
  'application/x-rar-compressed': 'rar',
  'application/x-rar': 'rar',
  'application/rar': 'rar',
  'application/x-7z-compressed': '7z',
  'application/x-tar': 'tar',
  'application/gzip': 'gz',
  'application/x-gzip': 'gz',
  'application/msword': 'doc',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
  'application/vnd.ms-excel': 'xls',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
  'application/vnd.ms-powerpoint': 'ppt',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx',
  'application/rtf': 'rtf',
  'text/plain': 'txt',
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/gif': 'gif',
  'image/webp': 'webp',
  'image/bmp': 'bmp',
  'image/tiff': 'tiff',
  'video/mp4': 'mp4',
  'video/webm': 'webm',
  'video/quicktime': 'mov',
  'audio/mpeg': 'mp3',
  'audio/ogg': 'ogg',
  'audio/mp4': 'm4a',
  'audio/wav': 'wav',
};

/** Lowercased, parameters (`; charset=…`) and whitespace stripped. */
const normalizeMime = (mimeType: string | null | undefined): string =>
  (mimeType || '').split(';')[0].trim().toLowerCase();

export const mediaKind = (mimeType: string | null | undefined): MediaKind => {
  const mime = normalizeMime(mimeType);
  if (!mime) return 'file';
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('video/')) return 'video';
  if (mime.startsWith('audio/')) return 'audio';
  if (mime === 'application/pdf') return 'pdf';
  if (ARCHIVE_MIMES.has(mime)) return 'archive';
  if (mime.startsWith('application/')) return 'document';
  return 'file';
};

/** i18n key for the fallback label. The four original keys are unchanged. */
export const mediaLabelKey = (mimeType: string | null | undefined): string => {
  switch (mediaKind(mimeType)) {
    case 'image': return 'chatPreview.media.photo';
    case 'video': return 'chatPreview.media.video';
    case 'audio': return 'chatPreview.media.audio';
    case 'pdf': return 'chatPreview.media.pdf';
    case 'archive': return 'chatPreview.media.archive';
    case 'document': return 'chatPreview.media.document';
    default: return 'chatPreview.media.file';
  }
};

export const mediaExtension = (mimeType: string | null | undefined): string | null => {
  const mime = normalizeMime(mimeType);
  if (!mime) return null;
  if (MIME_EXTENSIONS[mime]) return MIME_EXTENSIONS[mime];
  // A short, clean subtype is a usable extension (`image/heic` -> `heic`).
  // Anything longer or hyphenated (`x-rar-compressed`) is not, and inventing
  // one there is worse than having none.
  const subtype = mime.split('/')[1] || '';
  return /^[a-z0-9]{1,5}$/.test(subtype) ? subtype : null;
};

/**
 * What to show on the file card.
 *
 * `media.file_name` is NULL for every photo and for ~4% of documents, so a
 * name is synthesised in the shape Phase 2's `Content-Disposition` will use:
 * `file-<message_id>[.ext]`. `messageId` is the TELEGRAM message id — the one
 * the bubble tooltip and the `t.me` link already show, so a reviewer can match
 * a downloaded file back to a message. Returns null when there is no name and
 * no id to build one from; the caller falls back to the type label.
 */
export const mediaDisplayName = (
  media: MediaFile | null | undefined,
  messageId: number | null | undefined
): string | null => {
  const name = media?.file_name?.trim();
  if (name) return name;
  if (!messageId) return null;
  const ext = mediaExtension(media?.mime_type);
  return ext ? `file-${messageId}.${ext}` : `file-${messageId}`;
};
